import type { FleetUser } from '@cobliteam/api-lib/src/__generated__/graphql';
import type { IconName } from '@cobliteam/colib-ui/Icon';
import type { Coordinate, Sort } from '.';
import type { UUID } from './identifier';

type GeofenceColor = 'RED' | 'GREEN' | 'BLUE' | 'YELLOW' | 'PURPLE' | 'TOMATO' | 'LIME' | 'SKY' | 'GOLD' | 'PINK' | 'INDIGO' | 'BROWN' | 'TEAL' | 'ORANGE' | 'DEFAULT';

type GeofenceDefType = {
  label: string;
  iconName: IconName;
  color: 'primary' | 'purple' | 'sky' | 'tomato' | 'success' | 'brown' | 'default' | 'warning';
  cssColor: string;
};

enum GeofenceType {
  BASE = 'BASE',
  CLIENT = 'CLIENT',
  OTHER = 'OTHER',
  HEADQUARTER = 'HEADQUARTER',
  SUPPLY = 'SUPPLY',
  MAINTENANCE = 'MAINTENANCE',
  STATE = 'STATE',
  CITY = 'CITY',
  PATH = 'PATH',
  DEFAULT = 'DEFAULT', // Fallback
}

enum GeofenceFormat {
  CIRCLE = 'CIRCLE',
  RECTANGLE = 'RECTANGLE',
  DRAWING = 'DRAWING',
}

enum PointOnMapType {
  DESTINATION = 'DESTINATION',
  GEOFENCE = 'GEOFENCE',
  SEARCH = 'SEARCH',
}

export const GEOFENCE_DRAW_HINT_COLOR = '#80838d';

export const GeofencesDef: Record<GeofenceType, GeofenceDefType> = {
  [GeofenceType.BASE]: {
    label: 'Base',
    iconName: 'house',
    color: 'primary',
    cssColor: '#1c53e6',
  },
  [GeofenceType.HEADQUARTER]: {
    label: 'Sede',
    iconName: 'building-2',
    color: 'purple',
    cssColor: '#8145b5',
  },
  [GeofenceType.SUPPLY]: {
    label: 'Abastecimento',
    iconName: 'fuel',
    color: 'sky',
    cssColor: '#00749e',
  },
  [GeofenceType.MAINTENANCE]: {
    label: 'Manutenção',
    iconName: 'traffic-cone',
    color: 'tomato',
    cssColor: '#d13415',
  },
  [GeofenceType.CLIENT]: {
    label: 'Cliente',
    iconName: 'user',
    color: 'success',
    cssColor: '#008573',
  },
  [GeofenceType.PATH]: {
    label: 'Rota',
    iconName: 'route',
    color: 'brown',
    cssColor: '#815e46',
  },
  [GeofenceType.STATE]: {
    label: 'Estado',
    iconName: 'map-pinned',
    color: 'default',
    cssColor: '#60646c',
  },
  [GeofenceType.CITY]: {
    label: 'Cidade',
    iconName: 'map-pinned',
    color: 'default',
    cssColor: '#60646c',
  },
  [GeofenceType.OTHER]: {
    label: 'Outro',
    iconName: 'map-pin',
    color: 'warning',
    cssColor: '#cc4e00',
  },
  [GeofenceType.DEFAULT]: {
    label: 'Não identificado',
    iconName: 'map-pin',
    color: 'default',
    cssColor: '#60646c',
  },
};

const GeofenceFormatIcons: Record<GeofenceFormat, IconName> = {
  [GeofenceFormat.CIRCLE]: 'circle-dashed',
  [GeofenceFormat.RECTANGLE]: 'square-dashed',
  [GeofenceFormat.DRAWING]: 'square-dashed-mouse-pointer',
};

const GeofenceTranslate = {
  [GeofenceFormat.CIRCLE]: 'círculo',
  [GeofenceFormat.RECTANGLE]: 'retângulo',
  [GeofenceFormat.DRAWING]: 'desenho',
};

interface GeofenceFeatureCollection {
  type: 'FeatureCollection';
  features: [
    {
      type: 'Feature';
      geometry: GeofenceMultiPolygon | GeofenceGeometryPolygon;
      properties: GeofenceProperties;
    },
  ];
}

interface GeofenceMultiPolygon {
  type: 'MultiPolygon';
  coordinates: [number[][][]];
}

interface GeofenceGeometryPolygon {
  type: 'Polygon';
  coordinates: [number[][]];
}

interface GeofenceGeometryPoint {
  type: 'Point';
  coordinates: [number, number];
}

interface GeofenceProperties {
  id: UUID;
  type: GeofenceType;
}

interface GeofenceFilter {
  page?: number;
  search?: string;
  size?: number;
  types?: GeofenceType[];
  sort?: Sort[];
  type?: GeofenceType;
  createdByIds?: FleetUser['id'][];
  createdAtStart?: number;
  createdAtEnd?: number;
  unpaged?: boolean;
}

interface GeofenceMapPoint {
  type: 'Feature';
  geometry: GeofenceGeometryPoint;
  properties: GeofenceProperties;
}

interface GeofenceResume {
  id: UUID;
  lat: number;
  lng: number;
  type: GeofenceType;
  color?: GeofenceColor;
}

interface PlainGeofence {
  id: UUID;
  name: string;
  type: GeofenceType;
  format?: GeofenceFormat;
  radius?: number;
  address: string;
  geometryPoint: GeofenceGeometryPoint;
  geometryPolygon: GeofenceGeometryPolygon;
  area: number;
  color?: GeofenceColor;
  forceVisibility?: boolean;
  idIbgeEstado?: number;
  idIbgeMunicipio?: number;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: FleetUser;
}

interface PointOnMap {
  address?: string;
  center?: Coordinate;
  type?: PointOnMapType;
  geometryPolygon?: GeofenceGeometryPolygon;
}

interface Geofence extends PlainGeofence {
  geometry: GeofenceGeometryPolygon;
  properties: GeofenceProperties;
}

interface GeofenceValidationQuery {
  name: string;
  address: string;
}

export type {
  Geofence,
  GeofenceColor,
  GeofenceDefType,
  GeofenceFeatureCollection,
  GeofenceFilter,
  GeofenceGeometryPoint,
  GeofenceGeometryPolygon,
  GeofenceMapPoint,
  GeofenceMultiPolygon,
  GeofenceProperties,
  GeofenceResume,
  GeofenceValidationQuery,
  PlainGeofence,
  PointOnMap,
};
export { GeofenceFormat, GeofenceFormatIcons, GeofenceTranslate, GeofenceType, PointOnMapType };
